import * as React from 'react';
import pluralize from 'pluralize';
import { capitalize } from 'lodash';

import Button from 'components/ButtonDeprecated';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import Menu from 'components/Menu';
// @ts-ignore
import TargetFilterMenuItem from 'chat_v2/components/TargetFilterMenuItem';

import AppleImage from 'assets/images/roles/teacher.svg';
import BellImage from 'assets/images/bell.svg';
import HeartImage from 'assets/images/roles/parent.svg';
import PencilImage from 'assets/images/roles/student.svg';
import EveryoneImage from 'assets/images/roles/everyone.svg';

import { ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity, ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity_recipient_filters, ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity_recipient_filters_values } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';
import { ComposerDispatchContext, ComposerState } from 'chat_v2/ComposerContext';

type Props = {
  filter: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity_recipient_filters,
  entity: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity,
  dataTestSelector?: string,
  composerState: ComposerState,
};

type State = {
  menuIsOpen: boolean,
};

const roleToIcon: { [role: string]: string } = {
  student: PencilImage,
  parent: HeartImage,
  teacher: AppleImage,
  admin: BellImage,
};

export default class TargetFilterMenu extends React.Component<Props, State> {
  state = {
    menuIsOpen: false,
  };
  
  renderFilterMenu = (filterType: string, filterValues: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity_recipient_filters_values[]) => {
    const { entity, composerState, dataTestSelector } = this.props;
    const { recipient } = entity;
    const uuidProp = recipient.type === 'group' ? { groupUuid: recipient.uuid } : { orgUuid: recipient.uuid };
    const recipientIsSelected = recipient.uuid in composerState.recipients;

    return (
      <Menu className="RecipientItem-menu" hideOnSelect={false}>
        <ComposerDispatchContext.Consumer>
          {composerDispatch => {
            const selectedFilters = ((composerState.filters[recipient.uuid] || {})[filterType] || []);
            return (
              <>
                <TargetFilterMenuItem
                  dataTestSelector={dataTestSelector}
                  filter={{ type: filterType, value: { id: 'all', label: 'Everyone' }}}
                  imageSrc={EveryoneImage}
                  isSelected={recipientIsSelected && selectedFilters.length === 0}
                  onClick={() => {
                    if (!recipientIsSelected) {
                      composerDispatch({ type: 'addRecipient', entity });
                      EventTracking.trackKey('announcements.share_to.role.select.click', { context: 'everyone' });
                    } else if (selectedFilters.length === 0) {
                      composerDispatch({ type: 'removeRecipient', uuid: recipient.uuid });
                      EventTracking.trackKey('announcements.share_to.role.unselect.click', { context: 'everyone' });
                    } else if (selectedFilters.length > 0) {
                      selectedFilters.forEach(filterValue => EventTracking.trackKey('announcements.share_to.role.unselect.click', { context: filterValue }));
                      composerDispatch({ type: 'removeAllFilters', recipientUuid: recipient.uuid });
                    }
                  }}
                  {...uuidProp}
                />
                {filterValues.map(value => {
                  const isSelected = selectedFilters.includes(value.key);
                  return (
                    <TargetFilterMenuItem
                      key={value.key}
                      filter={{ type: filterType, value: { id: value.key, label: value.label }}}
                      imageSrc={roleToIcon[value.key]}
                      isSelected={isSelected}
                      dataTestSelector={dataTestSelector}
                      onClick={() => {
                        if (!recipientIsSelected) {
                          composerDispatch({ type: 'addRecipient', entity });
                        }
                        composerDispatch({ type: isSelected ? 'removeFilter' : 'addFilter', recipientUuid: recipient.uuid, filterType, filterValue: value.key });
                        EventTracking.trackKey(`announcements.share_to.role.${isSelected ? 'un' : ''}select.click`, { context: value.key });
                      }}
                      {...uuidProp}
                    />
                  )
                  })}
              </>
            );
          }}
        </ComposerDispatchContext.Consumer>
      </Menu>
    );
  };

  toggleMenu = (event: React.MouseEvent) => {
    event.stopPropagation();
    (this.refs.trigger as LayerTrigger).toggleLayer();
  };

  selectedFilterText = () => {
    const { composerState, entity, filter } = this.props;

    const selectedFilters = ((composerState.filters[entity.recipient.uuid] || {})[filter.key] || []);

    if (selectedFilters.length === 0) {
      return 'Everyone';
    } else if (selectedFilters.length < 3) {
      return selectedFilters.map(filterValue => capitalize(pluralize(filterValue, 2))).join(', ');
    } else {
      return `${selectedFilters.length} ${pluralize(filter.key, 2).toLowerCase()}`;
    }
  }

  render() {
    const { filter } = this.props;

    const setMenuIsOpen = (menuIsOpen: boolean) => {
      this.setState({ menuIsOpen });
      EventTracking.trackKey(`announcements.share_to.refine_recipients.role.${menuIsOpen ? 'close' : 'open'}`);
    }

    return (
      <LayerTrigger
        ref="trigger"
        trigger="manual"
        hideLayerOnScroll
        layer={this.renderFilterMenu(filter.key, filter.values)}
        placement="bottom"
        alignment="left"
        onHide={() => setMenuIsOpen(false)}
        onShow={() => setMenuIsOpen(true)}>
        <Button
          onClick={this.toggleMenu}
          size="xxxsmall"
          color={this.state.menuIsOpen ? 'mascot' : 'paper'}
          weight="normal"
          data-test-selector={this.props.dataTestSelector}
          className="RecipientItem-button">
          <MediaObject inline figure="right" gutter="xsmall">
            {this.selectedFilterText()}
            <Glyph name="chevron-down" size="xxsmall" solo />
          </MediaObject>
        </Button>
      </LayerTrigger>
    );
  }
}
